/**
 * Pakiety vertykalne (docs/PROMPTING.md § Vertical packs).
 *
 * Jedno miejsce, z ktorego:
 *  - renderuje sie panel UI (`control` + `options`),
 *  - kompilator promptu bierze fragmenty (`promptFragment`, partycjonowane po
 *    `constraintClass`: miekkie -> sekcja 9, twarde -> sekcje 11/12),
 *  - biora sie checki QA i guardraile opisow.
 *
 * DODANIE PARAMETRU = DODANIE JEDNEGO WPISU DESKRYPTORA. Bez zmian w
 * kompilatorze i bez zmian w UI.
 *
 * Implementacje pakietow (`fashion.ts`, `pharma.ts`) rejestruja sie same przez
 * `registerVerticalPack`; ten plik jest tylko kontraktem i rejestrem.
 */

import type { z } from 'zod';
import type { ClientFacts, GenerationTier, ProductFacts, ProductImageRole, PromptTier } from '../api-types';
import type { SceneSpec, VerticalId } from '../scene/spec';
import { VERTICALS } from '../scene/spec';

/** Identyfikatory vertykali w stabilnej kolejnosci. */
export const VERTICAL_IDS: readonly VerticalId[] = VERTICALS;

// ─────────────────────────────────────────────────────────────────────────────
// Deskryptory parametrow
// ─────────────────────────────────────────────────────────────────────────────

export const PARAM_CONTROLS = ['enum', 'bool', 'slider', 'color', 'text', 'multi'] as const;
export type ParamControl = (typeof PARAM_CONTROLS)[number];

/**
 * `hard` = wszystko, za co dzial prawny lub brandowy klienta moze odrzucic
 * asset; `soft` = kwestie gustu (docs/VERTICALS.md § Regula podzialu).
 */
export const CONSTRAINT_CLASSES = ['hard', 'soft'] as const;
export type ConstraintClass = (typeof CONSTRAINT_CLASSES)[number];

/** Kontekst, ktory kompilator podaje deskryptorom. */
export type VerticalPromptContext = {
  readonly spec: SceneSpec;
  readonly vertical: VerticalId;
  readonly tier: PromptTier;
  readonly product: ProductFacts;
  readonly client: ClientFacts;
  /** Caly `spec.verticalParams` — deskryptor moze zajrzec do sasiednich pol. */
  readonly params: Readonly<Record<string, unknown>>;
};

/**
 * Jeden parametr vertykalny.
 *
 * `value` jest `unknown`, bo kompilator iteruje po deskryptorach generycznie;
 * kazdy deskryptor sam zaweza typ swojej wartosci (`paramsSchema` gwarantuje
 * ksztalt, bo `verticalParams` przeszlo walidacje).
 */
export type ParamDescriptor = {
  readonly label: string;
  readonly control: ParamControl;
  readonly options?: readonly string[];
  readonly constraintClass: ConstraintClass;
  /** Fragment promptu albo `null`, gdy wartosc nie generuje tekstu. */
  readonly promptFragment: (value: unknown, ctx: VerticalPromptContext) => string | null;
  /** Wpisy do sekcji `DO_NOT` wynikajace z tej wartosci. */
  readonly forbidFragments?: (value: unknown, ctx: VerticalPromptContext) => readonly string[];
  /**
   * Nazwy parametrow, z ktorymi ten parametr *moze* wejsc w konflikt.
   *
   * To wylacznie STATYCZNA PODPOWIEDZ dla UI (podswietlenie powiazanych
   * kontrolek) i dla afordancji `critique`. NIE generuje ostrzezen — sama
   * obecnosc dwoch parametrow nie jest sprzecznoscia, bo w pelni wypelnionej
   * scenie ustawione jest niemal wszystko. Realne sprzecznosci deklaruje
   * `conflicts`, ktory patrzy na WARTOSCI.
   */
  readonly conflictsWith?: readonly string[];
  /**
   * Realne sprzecznosci wartosci. Wywolywane tylko gdy ten parametr jest
   * ustawiony; zwraca pary z komunikatem dla operatora.
   *
   * Deklaruj sprzecznosc po JEDNEJ stronie pary — `detectParamConflicts`
   * deduplikuje symetryczne zgloszenia, ale jednostronna deklaracja jest
   * czytelniejsza i tansza w utrzymaniu.
   */
  readonly conflicts?: (
    value: unknown,
    params: Readonly<Record<string, unknown>>,
  ) => readonly { readonly with: string; readonly message: string }[];
  /** Domysly per `products.product_type`. */
  readonly defaultsByProductType?: Readonly<Record<string, unknown>>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Checki QA
// ─────────────────────────────────────────────────────────────────────────────

export const QA_CHECK_IDS = [
  'label_text_unchanged',
  'no_forbidden_props',
  'no_invented_text',
  'silhouette_integrity',
  'hardware_color',
  'dimensions_and_sharpness',
] as const;
export type QaCheckId = (typeof QA_CHECK_IDS)[number];

/**
 * `vision` = jedno tanie wywolanie modelu wizyjnego (koszt w `cost_ledger` z
 * `phase='qa'`); `image_info` = darmowe `env.IMAGES.info()` + heurystyka.
 */
export const QA_CHECK_KINDS = ['vision', 'image_info'] as const;
export type QaCheckKind = (typeof QA_CHECK_KINDS)[number];

export const QA_SEVERITIES = ['info', 'warning', 'blocking'] as const;
export type QaSeverity = (typeof QA_SEVERITIES)[number];

/** Kontekst decyzji "czy ten check dotyczy tej generacji". */
export type QaCheckContext = {
  readonly spec: SceneSpec;
  readonly vertical: VerticalId;
  readonly tier: GenerationTier;
  readonly params: Readonly<Record<string, unknown>>;
};

export type QaCheck = {
  readonly id: QaCheckId;
  readonly label: string;
  readonly kind: QaCheckKind;
  /**
   * Tiery, na ktorych check sie uruchamia. Domyslnie tylko mastery i upscale —
   * NIGDY na 9 podgladach (docs/PIPELINE.md § Bramka QA).
   */
  readonly tiers: readonly GenerationTier[];
  /** `blocking` = blokuje `assets.approved_at` do recznego override. */
  readonly severity: QaSeverity;
  /** Dodatkowy warunek ze specu, np. `labelPolicy === 'locked_exact'`. */
  readonly appliesWhen?: (ctx: QaCheckContext) => boolean;
  /** Rola referencji, z ktora porownujemy wynik (np. `label`, `primary`). */
  readonly comparesToRefRole?: ProductImageRole;
  /** Instrukcja dla modelu wizyjnego; `undefined` dla checkow `image_info`. */
  readonly visionInstruction?: string;
};

/** Domyslny zestaw tierow dla checkow QA. */
export const QA_DEFAULT_TIERS: readonly GenerationTier[] = ['master', 'upscale_ai', 'upscale_ext'];

/** Czy check ma sie wykonac dla danego kontekstu. */
export function qaCheckApplies(check: QaCheck, ctx: QaCheckContext): boolean {
  if (!check.tiers.includes(ctx.tier)) return false;
  if (check.appliesWhen === undefined) return true;
  return check.appliesWhen(ctx);
}

// ─────────────────────────────────────────────────────────────────────────────
// Guardraile opisow
// ─────────────────────────────────────────────────────────────────────────────

export type CaptionGuardrails = {
  /** Czasowniki claimowe (leczy, zapobiega, ...) — lint po stronie klienta i serwera. */
  readonly forbiddenVerbs: readonly string[];
  /** Disclaimery doklejane wg `rxOtcClass`. */
  readonly requiredDisclaimers?: readonly string[];
};

// ─────────────────────────────────────────────────────────────────────────────
// Pakiet
// ─────────────────────────────────────────────────────────────────────────────

export type VerticalPack = {
  readonly id: VerticalId;
  /** np. `'fashion@1'` — trafia do `generations.template_version`. */
  readonly templateVersion: string;
  /** Schemat `FashionParams` / `PharmaParams`. */
  readonly paramsSchema: z.ZodType;
  /** Klucz = nazwa parametru w `spec.verticalParams`. */
  readonly paramDescriptors: Readonly<Record<string, ParamDescriptor>>;
  /** Twarde klauzule doklejane do sekcji `CONSTRAINTS` zawsze. */
  readonly defaultConstraints: readonly string[];
  /** Globalna lista negatywna vertykalu (sekcja `DO_NOT`). */
  readonly defaultForbid: readonly string[];
  readonly qaChecks: readonly QaCheck[];
  readonly captionGuardrails: CaptionGuardrails;
};

// ─────────────────────────────────────────────────────────────────────────────
// Rejestr
// ─────────────────────────────────────────────────────────────────────────────

const REGISTRY = new Map<VerticalId, VerticalPack>();

/**
 * Rejestracja pakietu. Wolana raz, na poziomie modulu, w
 * `src/shared/verticals/{fashion,pharma}.ts`. Powtorna rejestracja tego samego
 * `id` nadpisuje wpis (wygodne w testach), ale loguje przez wyjatek, gdy `id`
 * nie jest znanym vertykalem — literowka w `id` nie moze przejsc cicho.
 */
export function registerVerticalPack(pack: VerticalPack): void {
  if (!VERTICAL_IDS.includes(pack.id)) {
    throw new TypeError(`Unknown vertical id: "${String(pack.id)}"`);
  }
  REGISTRY.set(pack.id, pack);
}

/** Pakiet albo `null`, gdy nie zarejestrowany. */
export function tryGetVertical(id: VerticalId): VerticalPack | null {
  return REGISTRY.get(id) ?? null;
}

/**
 * Pakiet vertykalny. Rzuca, gdy pakiet nie jest zarejestrowany — to blad
 * konfiguracji (brak importu `src/shared/verticals/index.ts`), nie stan
 * runtime'u do obslugi.
 */
export function getVertical(id: VerticalId): VerticalPack {
  const pack = REGISTRY.get(id);
  if (pack === undefined) {
    throw new TypeError(
      `Vertical pack "${id}" is not registered — import src/shared/verticals/index.ts before use`,
    );
  }
  return pack;
}

/** Zarejestrowane pakiety, posortowane po `id` (determinizm). */
export function listVerticalPacks(): readonly VerticalPack[] {
  return VERTICAL_IDS.map((id) => REGISTRY.get(id)).filter((pack): pack is VerticalPack => pack !== undefined);
}

/** Tylko dla testow: czysci rejestr. */
export function resetVerticalRegistry(): void {
  REGISTRY.clear();
}

/**
 * Deskryptory pakietu w USTALONEJ kolejnosci (posortowane po nazwie parametru).
 * Kompilator promptu MUSI uzywac tej funkcji zamiast `Object.keys` — kolejnosc
 * kluczy obiektu nie jest kontraktem, a determinizm promptu jest.
 */
export function sortedParamEntries(
  pack: VerticalPack,
): readonly { readonly name: string; readonly descriptor: ParamDescriptor }[] {
  return Object.keys(pack.paramDescriptors)
    .slice()
    .sort()
    .map((name) => ({ name, descriptor: pack.paramDescriptors[name] }))
    .filter(
      (entry): entry is { name: string; descriptor: ParamDescriptor } => entry.descriptor !== undefined,
    );
}

/** Deskryptory danej klasy (twarde / miekkie), w ustalonej kolejnosci. */
export function paramEntriesByClass(
  pack: VerticalPack,
  constraintClass: ConstraintClass,
): readonly { readonly name: string; readonly descriptor: ParamDescriptor }[] {
  return sortedParamEntries(pack).filter((entry) => entry.descriptor.constraintClass === constraintClass);
}

/**
 * Realne sprzecznosci WARTOSCI parametrow — do `warnings` kompilatora i do
 * afordancji `critique`. Sprzecznosci sa deklarowane w danych (`conflicts`),
 * nie interpretowane przez model.
 *
 * Deduplikacja: para (A,B) jest raportowana raz, nawet gdy oba deskryptory
 * deklaruja ja niezaleznie. Klucz pary jest posortowany, wiec wynik nie zalezy
 * od kolejnosci iteracji — to warunek determinizmu promptu.
 */
export function detectParamConflicts(
  pack: VerticalPack,
  params: Readonly<Record<string, unknown>>,
): readonly { readonly param: string; readonly conflictsWith: string; readonly message: string }[] {
  const byPair = new Map<string, { param: string; conflictsWith: string; message: string }>();

  for (const entry of sortedParamEntries(pack)) {
    const detect = entry.descriptor.conflicts;
    if (detect === undefined) continue;
    const value = params[entry.name];
    if (value === undefined) continue;

    for (const hit of detect(value, params)) {
      // Nazwa pary posortowana, zeby A-vs-B i B-vs-A trafily w ten sam klucz.
      const [first, second] = [entry.name, hit.with].sort() as [string, string];
      const key = `${first} ${second}`;
      if (byPair.has(key)) continue;
      byPair.set(key, { param: first, conflictsWith: second, message: hit.message });
    }
  }

  return [...byPair.keys()].sort().map((key) => byPair.get(key)!);
}
